import { ref, computed, watchEffect, onUnmounted } from 'vue'

export type ThemePreference = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'sr-theme-preference'
const DARK_CLASS = 'p-dark'

const preference = ref<ThemePreference>(
  (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'system',
)

const systemDark = ref(window.matchMedia('(prefers-color-scheme: dark)').matches)

function applyTheme(dark: boolean) {
  document.documentElement.classList.toggle(DARK_CLASS, dark)
}

/** Shared composable — all consumers share the same reactive state. */
export function useTheme() {
  const isDark = computed(() =>
    preference.value === 'system' ? systemDark.value : preference.value === 'dark',
  )

  const icon = computed(() => {
    if (preference.value === 'light') return 'pi pi-sun'
    if (preference.value === 'dark') return 'pi pi-moon'
    return 'pi pi-desktop'
  })

  const label = computed(() => {
    if (preference.value === 'light') return 'Light theme'
    if (preference.value === 'dark') return 'Dark theme'
    return 'System theme'
  })

  function setTheme(value: ThemePreference) {
    preference.value = value
    localStorage.setItem(STORAGE_KEY, value)
  }

  function cycleTheme() {
    const order: ThemePreference[] = ['light', 'dark', 'system']
    const next = order[(order.indexOf(preference.value) + 1) % order.length]!
    setTheme(next)
  }

  // Listen for OS preference changes
  const mql = window.matchMedia('(prefers-color-scheme: dark)')
  const onMediaChange = (e: MediaQueryListEvent) => {
    systemDark.value = e.matches
  }
  mql.addEventListener('change', onMediaChange)
  onUnmounted(() => mql.removeEventListener('change', onMediaChange))

  // Apply theme reactively
  watchEffect(() => applyTheme(isDark.value))

  return { preference, isDark, icon, label, setTheme, cycleTheme }
}
